// i18n: translated
/**
 * RequirePermission — RBAC ルートガード (RBAC P1 F10)
 *
 * 直接 URL アクセス時でも、権限のないユーザーにはページ本体を描画せず
 * 「権限がありません」画面を表示する。判定ロジックは utils/permissionGuard の純関数に委譲。
 *
 * 注: これは UX / defense-in-depth であり、最終的な認可は backend が enforce する。
 */
import type { ReactNode } from 'react';
import type { PermissionArea, PermissionAction, UserRole } from '@/types';
import { usePermission } from '@/hooks/usePermission';
import { isGuardAllowed } from '@/utils/permissionGuard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useT } from '@/i18n';
import { shellNs } from '@/i18n/namespaces/shell';

interface RequirePermissionProps {
  role?: UserRole | UserRole[];
  anyArea?: PermissionArea[];
  area?: PermissionArea;
  action?: PermissionAction;
  children: ReactNode;
}

/** 権限なし時に表示するカード (AdminPage の既存 403 表示に準拠) */
function NoPermission() {
  const t = useT(shellNs);
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          {t('permission.noPermissionTitle')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('permission.noPermissionBody')}
        </p>
      </div>
    </div>
  );
}

export default function RequirePermission({
  role,
  anyArea,
  area,
  action,
  children,
}: RequirePermissionProps) {
  const t = useT(shellNs);
  const { role: currentRole, canRead, canWrite, canExecute, status, retry } = usePermission();

  // platform のロール境界はテナントの実効権限取得に依存しない。
  if (!role && status === 'loading') return <LoadingSpinner />;
  if (!role && status === 'error') {
    return (
      <div role="alert" className="p-6 text-center">
        <p>{t('permission.checkFailed')}</p>
        <button type="button" onClick={retry} className="mt-3 rounded border px-4 py-2">
          {t('permission.reload')}
        </button>
      </div>
    );
  }

  const allowed = isGuardAllowed(
    {
      role: currentRole,
      can: (a, act) =>
        act === 'write' ? canWrite(a) : act === 'execute' ? canExecute(a) : canRead(a),
    },
    { role, anyArea, area, action },
  );

  if (!allowed) return <NoPermission />;
  return <>{children}</>;
}
